import React, { useState, useEffect } from "react";
import { CloudflareStatusDiagram } from "../components/cloudflare-status-diagram";
import { Badge } from "@pgs-core/ui/components/badge";
import { ExternalLink, Loader2 } from "lucide-react";

// Structural subset of cloudflarestatus.com's /api/v2/summary.json.
interface StatusComponent {
  id: string;
  name: string;
  status: string;
}
interface StatusIncident {
  id: string;
  name: string;
  impact: string;
  shortlink: string;
}
interface StatusSummary {
  status: { indicator: string; description: string };
  components?: StatusComponent[];
  incidents?: StatusIncident[];
}

const INDICATOR_VARIANT: Record<string, "success" | "warning" | "destructive"> = {
  none: "success",
  minor: "warning",
  major: "destructive",
  critical: "destructive",
};

const PGS_COMPONENT_IDS = new Set([
  "57srcl8zcn7c", // Workers
  "vcpsk0w6g86k", // Workers Builds
  "z0yd3sxqjqdm", // D1
  "hb7g5sq2zz0h", // R2
  "vgxj684rcw7t", // Pages
  "dp8ppfycqxcs", // Authoritative DNS
  "7j656z7tqk7f", // DNS Updates
  "9p2qlpt19nqb", // SSL for SaaS
  "cghykwlwsmn5", // SSL Certificates
]);

export function InfrastructurePage() {
  const [data, setData] = useState<StatusSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("https://www.cloudflarestatus.com/api/v2/summary.json")
      .then((r) => r.json() as Promise<StatusSummary>)
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, []);

  const pgsComponents = data?.components?.filter((c) => PGS_COMPONENT_IDS.has(c.id)) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Live status for the services PGS Core depends on.
        </p>
        {data ? (
          <Badge variant={INDICATOR_VARIANT[data.status.indicator] ?? "secondary"}>
            {data.status.description}
          </Badge>
        ) : !error ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : null}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load Cloudflare status: {error}
        </div>
      )}

      {(data?.incidents?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-destructive">
            Active incidents
          </p>
          <ul className="space-y-2">
            {data?.incidents?.map((inc) => (
              <li key={inc.id} className="flex items-center justify-between text-sm">
                <span className="font-medium">{inc.name}</span>
                <div className="flex items-center gap-2">
                  <Badge variant={INDICATOR_VARIANT[inc.impact] ?? "secondary"}>
                    {inc.impact}
                  </Badge>
                  <a
                    href={inc.shortlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Details <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <CloudflareStatusDiagram components={pgsComponents ?? undefined} />

      {data && (
        <p className="text-right text-xs text-muted-foreground">
          Source:{" "}
          <a
            href="https://www.cloudflarestatus.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            cloudflarestatus.com
          </a>
        </p>
      )}
    </div>
  );
}
